/**
 * TrainingEffectivenessAdmin Component
 * 
 * Phase 3: Learning Impact Loop - Admin Aggregate View
 * 
 * Shows artifact-level engagement signals, NOT technician-level data.
 * This is for understanding which training helps, not for judging people.
 * 
 * TWEAK 1: Uses "engagement signals" terminology, not "effectiveness"
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, TrendingUp, TrendingDown, Minus, BarChart3, Sparkles, AlertCircle } from 'lucide-react';
import { useAggregateEffectSignals } from '@/hooks/useLearningImpactLoop';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import { PHASE3_CONSTANTS } from '@/types/training';

interface TrainingEffectivenessAdminProps {
  shopId?: string;
}

export function TrainingEffectivenessAdmin({ shopId }: TrainingEffectivenessAdminProps) {
  const [domainFilter, setDomainFilter] = useState<string>('all');

  // Fetch aggregate effect signals
  const { data: summaries, isLoading } = useAggregateEffectSignals({
    domainKey: domainFilter !== 'all' ? domainFilter : undefined,
    minSamples: PHASE3_CONSTANTS.MINIMUM_SAMPLE_THRESHOLD,
    limit: 20,
  });

  // Fetch artifact titles for display
  const { data: artifacts } = useQuery({
    queryKey: ['training-artifacts', 'titles', summaries?.map(s => s.artifact_id)],
    queryFn: async () => {
      if (!summaries || summaries.length === 0) return {};

      const { data } = await supabase
        .from('training_artifacts')
        .select('id, title, provider_name')
        .in('id', summaries.map(s => s.artifact_id));

      return (data || []).reduce((acc, a) => {
        acc[a.id] = { title: a.title, provider: a.provider_name };
        return acc;
      }, {} as Record<string, { title: string; provider: string }>);
    },
    enabled: !!summaries && summaries.length > 0,
  });

  // Fetch domains for filter
  const { data: domains } = useQuery({
    queryKey: ['training-domains'],
    queryFn: async () => {
      const { data } = await supabase
        .from('training_artifact_domain_map')
        .select('domain_key')
        .limit(100);

      const uniqueDomains = [...new Set((data || []).map(d => d.domain_key))];
      return uniqueDomains.sort();
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const hasSummaries = summaries && summaries.length > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Training Engagement Signals
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Aggregate view of how training correlates with improved work outcomes
          </p>
        </div>

        {/* Domain Filter */}
        <Select value={domainFilter} onValueChange={setDomainFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by domain" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Domains</SelectItem>
            {domains?.map(domain => (
              <SelectItem key={domain} value={domain}>
                {domain.replace(/_/g, ' ')}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Info Banner */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="py-3">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-primary mt-0.5 shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-primary">What you're seeing</p>
              <p className="text-muted-foreground mt-1">
                These signals show correlations between training engagement and work outcomes. 
                They help improve recommendations but are never used to score or rank technicians.
                Signals require at least {PHASE3_CONSTANTS.MINIMUM_SAMPLE_THRESHOLD} completed observations.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Signals Grid */}
      {!hasSummaries ? (
        <Card>
          <CardContent className="py-12 text-center">
            <BarChart3 className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="font-medium text-muted-foreground">No engagement signals yet</p>
            <p className="text-sm text-muted-foreground mt-1">
              Signals appear after technicians complete training and observation windows close
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {summaries.map((summary) => {
            const artifactInfo = artifacts?.[summary.artifact_id];
            const directionIcon = 
              summary.dominant_direction === 'positive' ? <TrendingUp className="h-4 w-4 text-green-600" /> :
              summary.dominant_direction === 'negative' ? <TrendingDown className="h-4 w-4 text-red-500" /> :
              <Minus className="h-4 w-4 text-muted-foreground" />;

            const directionLabel =
              summary.dominant_direction === 'positive' ? 'Positive impact observed' :
              summary.dominant_direction === 'negative' ? 'Mixed results observed' :
              'Neutral / Insufficient evidence';

            return (
              <Card key={summary.artifact_id}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <CardTitle className="text-base truncate">
                        {artifactInfo?.title || 'Unknown Training'}
                      </CardTitle>
                      {artifactInfo?.provider && (
                        <CardDescription className="text-xs mt-1">
                          {artifactInfo.provider}
                        </CardDescription>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {directionIcon}
                      <Badge
                        variant={
                          summary.dominant_direction === 'positive' ? 'default' :
                          summary.dominant_direction === 'negative' ? 'destructive' :
                          'secondary'
                        }
                        className={cn(
                          summary.dominant_direction === 'positive' && 'bg-green-600'
                        )}
                      >
                        {directionLabel}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-4">
                      <div>
                        <span className="text-muted-foreground">Engagements: </span>
                        <span className="font-medium">{summary.total_samples}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs text-green-600 border-green-600/30">
                          +{summary.positive_signals} signals
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {summary.neutral_signals} neutral
                        </Badge>
                        {summary.negative_signals > 0 && (
                          <Badge variant="outline" className="text-xs text-red-500 border-red-500/30">
                            -{summary.negative_signals} signals
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Confidence: {(summary.avg_confidence * 100).toFixed(0)}%
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Footer Note */}
      <p className="text-xs text-muted-foreground text-center">
        Signals decay naturally over time. Positive requires {Math.round(PHASE3_CONSTANTS.POSITIVE_THRESHOLD * 100)}% favorable outcomes. 
        Negative requires {Math.round(PHASE3_CONSTANTS.NEGATIVE_THRESHOLD * 100)}% unfavorable outcomes.
      </p>
    </div>
  );
}
